import { Link } from "@inertiajs/react"
import { ChevronDown } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CrewMemberCollabTimeline,
  provenanceLabel,
} from "@/components/crew/crew-member-collab-timeline"
import { BrandMissionCollabRowActions } from "@/components/missions/brand-mission-collab-row-actions"
import { CollabStatePill } from "@/components/missions/collab-state-pill"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { useOnline } from "@/hooks/use-online"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatRelativeIntl } from "@/lib/relative-time"
import { missionPath } from "@/routes"
import type { CrewMemberCollabRow as Row } from "@/types/crew"

/** States where a submission can exist — the content line shows from here. */
const CONTENT_STATES: Row["state"][] = [
  "accepted",
  "delivered",
  "posted",
  "completed",
]

/**
 * OKS-1038 — one collab on the crew member page (UX Spec §2 Row).
 *
 * Line 1: mission (link) + type · state pill · « Mis à jour ».
 * Line 2: gifting status (+ provenance) · content status.
 * Line 3: the mission panel's row actions (ship → queue link) + « Historique ».
 *
 * Everything is read from the server row — the gifting ladder, the content
 * status and the timeline are resolved in `Collabs::BrandRowSerializer`.
 */
export function CrewMemberCollabRow({
  row,
  articleRef,
}: {
  row: Row
  articleRef?: (node: HTMLElement | null) => void
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const online = useOnline()
  const locale = toAppLocale(i18n.language)
  const titleId = `crew-member-collab-${row.id}-title`
  const timelineId = `crew-member-collab-${row.id}-timeline`

  const updated = row.last_state_change_at
  const gifting = giftingText(t, row)
  const content =
    CONTENT_STATES.includes(row.state) || row.content.total > 0
      ? contentText(t, row.content)
      : null

  return (
    <article
      ref={articleRef}
      tabIndex={-1}
      aria-labelledby={titleId}
      className="motion-safe:animate-in motion-safe:fade-in flex flex-col gap-2 py-4 first:pt-0 last:pb-0 focus:outline-none"
      data-testid="crew-member-collab-row"
      data-collab-id={row.id}
      data-state={row.state}
    >
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between md:gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            id={titleId}
            href={missionPath(row.mission.id)}
            className="min-w-0 truncate font-medium hover:underline"
            title={row.mission.title}
          >
            {row.mission.title}
          </Link>
          <Badge variant="outline" className="shrink-0">
            {t(`missions.types.${row.mission.mission_type}`)}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <CollabStatePill state={row.state} />
          {updated && (
            <span
              className="text-muted-foreground text-xs whitespace-nowrap"
              title={new Date(updated).toLocaleString(locale)}
              data-testid="crew-member-collab-updated"
            >
              {t("crew.detail.collab.updated", {
                relative: formatRelativeIntl(updated, locale),
              })}
            </span>
          )}
        </div>
      </div>

      {row.state === "issue" && row.issue_note && (
        <p className="text-muted-foreground line-clamp-3 text-sm">
          {t("crew.detail.collab.timeline.note", { note: row.issue_note })}
        </p>
      )}

      {(gifting !== null || content !== null) && (
        <p
          className="text-muted-foreground flex flex-col gap-0.5 text-sm md:flex-row md:flex-wrap md:gap-x-2"
          data-testid="crew-member-collab-meta"
        >
          {gifting}
          {gifting && content && (
            <span aria-hidden="true" className="hidden md:inline">
              ·
            </span>
          )}
          {content && (
            <span data-testid="crew-member-collab-content">{content}</span>
          )}
        </p>
      )}

      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* UX Spec §3 — offline: every write trigger disabled, like the
              page's other write controls (`useOnline`). A disabled
              `<fieldset>` disables every button inside it at once, including
              any trigger a future flag adds; `contents` keeps the triggers
              direct flex items of this row, so the 375px wrap is unchanged.
              « Historique » stays outside: reading the timeline is not a write.
              The Shipments queue link is navigation, not a write, and a link
              is not a form control, so it stays enabled. */}
          <fieldset
            disabled={!online}
            className="contents"
            data-testid="crew-member-collab-actions"
          >
            <BrandMissionCollabRowActions
              row={row}
              missionId={row.mission.id}
              shipAction="queue_link"
            />
          </fieldset>
          <CollapsibleTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="max-md:min-h-11"
                aria-label={t("crew.detail.collab.history_aria", {
                  mission: row.mission.title,
                })}
                aria-controls={timelineId}
                data-testid="crew-member-collab-history-trigger"
              >
                {open
                  ? t("crew.detail.collab.history_hide")
                  : t("crew.detail.collab.history_show")}
                <ChevronDown
                  aria-hidden="true"
                  className="ml-1 size-4 data-[open=true]:rotate-180 motion-safe:transition-transform"
                  data-open={open}
                />
              </Button>
            }
          />
        </div>
        <CollapsibleContent id={timelineId} className="pt-3">
          <CrewMemberCollabTimeline
            steps={row.timeline}
            testId="crew-member-collab-timeline"
          />
        </CollapsibleContent>
      </Collapsible>
    </article>
  )
}

function giftingText(t: ReturnType<typeof useTranslation>["t"], row: Row) {
  const g = row.gifting
  if (!g) return null

  switch (g.status) {
    case "no_ship":
      return <span>{t("crew.detail.collab.gifting.no_ship")}</span>
    case "address_pending":
      return (
        <span className="text-amber-700 dark:text-amber-400">
          {t("crew.detail.collab.gifting.address_pending")}
        </span>
      )
    case "address_confirmed":
      return (
        <span>
          {t("crew.detail.collab.gifting.address_confirmed")}
          {g.confirmation_source && (
            <span className="text-muted-foreground/80">
              {" — "}
              {provenanceLabel(t, g.confirmation_source)}
            </span>
          )}
        </span>
      )
    case "shipped":
      return (
        <span>
          {t("crew.detail.collab.gifting.shipped")}
          {g.tracking_url ? (
            <>
              {" · "}
              <a
                href={g.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                aria-label={t("crew.detail.collab.gifting.track_parcel_aria")}
              >
                {t("crew.detail.collab.gifting.track_parcel")}
              </a>
            </>
          ) : g.tracking_number ? (
            <>
              {" · "}
              {t("crew.detail.collab.gifting.tracking_number", {
                number: g.tracking_number,
              })}
            </>
          ) : null}
        </span>
      )
    case "received":
      return <span>{t("crew.detail.collab.gifting.received")}</span>
    case "delivered":
      return <span>{t("crew.detail.collab.gifting.delivered")}</span>
    default:
      return null
  }
}

function contentText(
  t: ReturnType<typeof useTranslation>["t"],
  content: Row["content"],
): string {
  const vars = { n: content.total, cap: content.cap }
  switch (content.latest_state) {
    case "pending":
      return t("crew.detail.collab.content.pending", vars)
    case "approved":
      return t("crew.detail.collab.content.approved", vars)
    case "flagged":
      return t("crew.detail.collab.content.flagged", vars)
    default:
      return t("crew.detail.collab.content.none")
  }
}
